import React, { useState, useEffect, useMemo } from "react";
import { db } from "../../../db/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import AppSelect from "../../../components/AppSelect";
import { DateField, TimeField } from "../../../components/DateTimeFields";
import { HiOutlineXMark, HiOutlineCalendarDays } from "react-icons/hi2";
import { useAuth } from "../../../context/AuthContext";

const inputBase =
  "w-full px-4 py-3 sm:py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-base sm:text-sm focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-colors";

const EVENT_TYPES = ["Service", "Worship", "Planning", "Global", "Other"] as const;

interface EventModalProps {
  event: any | null;
  onSave: (event: any) => void | Promise<void>;
  onClose: () => void;
}

const EventModal: React.FC<EventModalProps> = ({ event, onSave, onClose }) => {
  const { t } = useTranslation();
  const { user, organizationId } = useAuth();
  const [title, setTitle] = useState(event?.title || "");
  const [description, setDescription] = useState(event?.description || "");
  const [date, setDate] = useState(event?.date || "");
  const [time, setTime] = useState(event?.time || "");
  const [type, setType] = useState(event?.type || "");
  const [groupOrMinistry, setGroupOrMinistry] = useState(
    event?.groupOrMinistry || ""
  );
  const [ministriesAndGroups, setMinistriesAndGroups] = useState<any[]>([]);
  const [isRecurring, setIsRecurring] = useState(event?.isRecurring || false);
  const [recurrencePattern, setRecurrencePattern] = useState(
    event?.recurrencePattern || ""
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchMinistriesAndGroups = async () => {
      if (!organizationId) return;
      try {
        const [ministriesSnap, groupsSnap] = await Promise.all([
          getDocs(
            query(collection(db, "ministries"), where("organizationId", "==", organizationId))
          ),
          getDocs(
            query(collection(db, "groups"), where("organizationId", "==", organizationId))
          ),
        ]);
        const ministries = ministriesSnap.docs.map((d) => ({
          id: d.id,
          name: d.data().name,
          kind: "ministry",
        }));
        const groups = groupsSnap.docs.map((d) => ({
          id: d.id,
          name: d.data().name,
          kind: "group",
        }));
        setMinistriesAndGroups([...ministries, ...groups]);
      } catch (err) {
        console.error(err);
      }
    };
    fetchMinistriesAndGroups();
  }, [organizationId]);

  const handleSave = async () => {
    if (!title.trim() || !date || !time || !type || !groupOrMinistry) {
      setError(t("fillRequiredFields"));
      return;
    }
    if (isRecurring && !recurrencePattern) {
      setError(t("selectRecurrencePattern"));
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        date,
        time,
        type,
        groupOrMinistry,
        isRecurring,
        recurrencePattern: isRecurring ? recurrencePattern : "",
        timestamp: serverTimestamp(),
        userId: user?.uid,
      });
    } catch (err) {
      console.error(err);
      setError(t("error") || "Something went wrong");
      setSaving(false);
    }
  };

  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5";

  const groupOptions = useMemo(
    () =>
      ministriesAndGroups.map((item) => ({
        value: item.id,
        label: item.name,
        badge: item.kind === "ministry" ? t("ministries") : t("groups"),
      })),
    [ministriesAndGroups, t]
  );

  const recurrenceOptions = useMemo(
    () => [
      { value: "Daily", label: t("daily") },
      { value: "Weekly", label: t("weekly") },
      { value: "Monthly", label: t("monthly") },
    ],
    [t]
  );

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-lg">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/80 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shrink-0">
              <HiOutlineCalendarDays className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 truncate">
              {event ? t("editEvent") : t("addEvent")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 pb-8 overflow-y-auto overscroll-contain flex-1 space-y-4">
          <div>
            <label className={labelClass}>{t("title")} *</label>
            <input
              maxLength={150}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputBase}
              placeholder={t("title")}
              autoFocus
            />
          </div>
          <div>
            <label className={labelClass}>{t("description")}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`${inputBase} min-h-[88px] resize-y`}
              placeholder={t("description")}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t("date")} *</label>
              <DateField value={date} onChange={setDate} disabled={saving} />
            </div>
            <div>
              <label className={labelClass}>{t("time")} *</label>
              <TimeField value={time} onChange={setTime} disabled={saving} />
            </div>
          </div>

          <div>
            <label className={labelClass}>{t("typeOfEvent")} *</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {EVENT_TYPES.map((value) => {
                const active = type === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setType(value)}
                    className={`min-h-[44px] px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors text-left
                      ${
                        active
                          ? "border-[#5932EA] bg-[#5932EA]/10 text-[#5932EA] ring-1 ring-[#5932EA]/30"
                          : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500"
                      }`}
                  >
                    {t(value.toLowerCase())}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t("groupOrMinistry")} *</label>
            <AppSelect
              value={groupOrMinistry}
              onChange={setGroupOrMinistry}
              options={groupOptions}
              placeholder={t("selectGroupOrMinistry")}
              searchable={groupOptions.length > 6}
              searchPlaceholder={t("search")}
              disabled={saving}
            />
          </div>

          <label className="flex items-center gap-3 min-h-[44px] cursor-pointer select-none">
            <input
              type="checkbox"
              id="recurring"
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-[#5932EA] focus:ring-[#5932EA]"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t("isRecurring")}
            </span>
          </label>

          {isRecurring && (
            <div>
              <label className={labelClass}>{t("recurrencePattern")} *</label>
              <AppSelect
                value={recurrencePattern}
                onChange={setRecurrencePattern}
                options={recurrenceOptions}
                placeholder={t("selectRecurrencePattern")}
                disabled={saving}
              />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
          )}
        </div>

        <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80 shrink-0 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-full sm:w-auto sm:flex-1 py-3 sm:py-2.5 rounded-xl font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
          >
            {t("cancel") || t("close")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto sm:flex-1 py-3 sm:py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t("saving") || t("save")}
              </>
            ) : (
              t("save")
            )}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default EventModal;
